import { useEffect, useState } from "react";
import { Bomb } from "./components/Bomb.js";
import { HUD } from "./components/HUD.js";
import {
  ActiveControls,
  EndControls,
  IdleControls,
} from "./components/Controls.js";
import { ExplosionOverlay } from "./components/ExplosionOverlay.js";
import { useAuth } from "./hooks/useAuth.js";
import { useGame } from "./hooks/useGame.js";

export function App() {
  const auth = useAuth();
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    if (auth.status === "ok" && auth.user) setBalance(auth.user.balance);
  }, [auth.status, auth.user]);

  const game = useGame(setBalance);

  if (auth.status !== "ok") {
    return (
      <div className="h-full flex items-center justify-center p-6 text-center">
        <div className="hud-chip max-w-sm">
          <div className="text-cs-orange font-bold uppercase tracking-wider text-xs mb-2">
            CSLame — Defuse
          </div>
          {auth.status === "loading" && <div>Connecting to Telegram…</div>}
          {auth.status === "idle" && <div>Initializing…</div>}
          {auth.status === "error" && (
            <>
              <div className="text-cs-red mb-3">{auth.error}</div>
              <button onClick={() => auth.refresh()} className="btn-secondary">
                Retry
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const s = game.state;
  const mult = s.phase === "active" ? s.multiplier : 1;
  const next = s.phase === "active" ? s.nextMultiplier : null;
  const picks = s.phase === "active" ? s.picks : s.phase === "busted" ? s.position : 0;
  const seed = s.phase === "active" ? s.seedHash : undefined;
  const revealed =
    s.phase === "active" || s.phase === "busted" || s.phase === "cashed_out"
      ? s.revealed
      : "";
  const position = s.phase === "active" ? s.picks : 0;
  const status: "idle" | "active" | "cashed_out" | "busted" = s.phase;

  const dangerDigits =
    s.phase === "active"
      ? s.dangerDigits
      : s.phase === "busted"
        ? new Set(s.bombDigits)
        : new Set<number>();

  const flashDigit = s.phase === "active" ? s.flashDigit : null;

  return (
    <div className="relative h-full w-full flex flex-col overflow-hidden">
      {/* HUD — fixed height */}
      <div className="shrink-0">
        <HUD
          balance={balance}
          multiplier={mult}
          nextMultiplier={next}
          picks={picks}
          seedHash={seed}
        />
      </div>

      {/* bomb — fills remaining space, scales to fit */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-1 py-1">
        <Bomb
          revealed={revealed}
          position={position}
          status={status}
          dangerDigits={dangerDigits}
          flashDigit={flashDigit}
          shaking={game.shaking}
          onPress={game.press}
        />
      </div>

      <ExplosionOverlay
        show={game.explosion}
        onDone={() => game.setExplosion(false)}
      />

      {game.error && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 hud-chip border-cs-red/60 text-cs-red text-xs z-30">
          {game.error}
        </div>
      )}

      {/* Controls — natural height, bottom */}
      <div className="shrink-0">
        {s.phase === "idle" && (
          <IdleControls
            balance={balance}
            disabled={balance < 1}
            onStart={(stake, difficulty) => {
              void game.start(stake, difficulty);
            }}
          />
        )}
        {s.phase === "active" && (
          <ActiveControls
            multiplier={s.multiplier}
            picks={s.picks}
            stake={s.stake}
            onCashout={() => {
              void game.cashout();
            }}
            disabled={false}
          />
        )}
        {s.phase === "cashed_out" && (
          <EndControls
            result={{
              label: "Defused! Payout",
              value: `+${Math.floor(s.payout).toLocaleString()} (×${s.multiplier.toFixed(2)})`,
              tone: "win",
            }}
            onReset={game.reset}
          />
        )}
        {s.phase === "busted" && (
          <EndControls
            result={{
              label: `Boom — ${s.bombDigits.length}/10 deadly at pos ${s.position + 1}`,
              value: `−${s.stake.toLocaleString()}`,
              tone: "loss",
            }}
            onReset={game.reset}
          />
        )}
      </div>
    </div>
  );
}
